import { Card, CollapseBar, Icon, IconButton } from '@/components/ui'
import { useT } from '@/i18n/useT'
import type { ExtractionHistory } from '@/types/invoiceAnalyser'

interface HistoryPanelProps {
  history: ExtractionHistory[]
  onItemClick?: (item: ExtractionHistory) => void
  onDeleteItem?: (documentId: string) => void
  open: boolean
  onToggle: () => void
}

const STATUS_LABELS: Record<ExtractionHistory['status'], { label: string; fg: string }> = {
  completed: { label: 'Done', fg: 'var(--ok)' },
  processing: { label: 'Running', fg: 'var(--warn)' },
  pending: { label: 'Waiting', fg: 'var(--muted)' },
  failed: { label: 'Error', fg: 'var(--err)' },
}

function formatWhen(isoString: string): string {
  const date = new Date(isoString)
  return Number.isNaN(date.getTime())
    ? isoString
    : new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
}

function formatMoney(amount: number | null | undefined, currency: string | null): string {
  if (amount == null) return '–'
  return `${new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)} ${currency || ''}`.trim()
}

/**
 * The design's "Verlauf" block: a collapse bar over compact rows, so recent
 * analyses stay reachable without taking a sidebar column.
 */
export function HistoryPanel({
  history,
  onItemClick,
  onDeleteItem,
  open,
  onToggle,
}: HistoryPanelProps) {
  const { t } = useT()
  const sorted = [...history].sort(
    (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
  )

  return (
    <>
      <CollapseBar
        label={t('History')}
        summary={t('{n} recently analysed documents').replace('{n}', String(sorted.length))}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <Card padding={14}>
          {sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 2px' }}>
              {t('No processed documents yet — upload an invoice.')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sorted.map((item, i) => {
                const status = STATUS_LABELS[item.status] ?? STATUS_LABELS.pending
                const canView = item.status === 'completed' && onItemClick != null
                return (
                  <div
                    key={item.documentId}
                    className="fx-card-hoverable"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 10px',
                      borderRadius: 8,
                      border: '1px solid var(--line2)',
                      background: i % 2 === 1 ? 'var(--zebra)' : 'transparent',
                    }}
                  >
                    <div
                      style={{ flex: 1, minWidth: 0, cursor: canView ? 'pointer' : 'default' }}
                      onClick={canView ? () => onItemClick?.(item) : undefined}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.filename}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--muted)',
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {(item.result?.supplier || item.error || t('No supplier recognised')) +
                          ' · ' +
                          formatWhen(item.uploadedAt)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>
                        {formatMoney(item.result?.total_gross, item.result?.currency ?? null)}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: status.fg }}>
                        {t(status.label)}
                      </div>
                    </div>
                    {canView && (
                      <IconButton
                        title={t('Show details')}
                        size="sm"
                        onClick={() => onItemClick?.(item)}
                      >
                        <Icon name="eye" size={15} />
                      </IconButton>
                    )}
                    {onDeleteItem && (
                      <IconButton
                        title={t('Delete')}
                        size="sm"
                        variant="danger"
                        onClick={() => onDeleteItem(item.documentId)}
                      >
                        <Icon name="trash" size={15} />
                      </IconButton>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}
    </>
  )
}
